import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import Axios from "axios";
import { Formik, Form, Field } from "formik";
import { Button } from "../../ui/Button";
import { TitleText } from "../../ui/Text";
import MeasuresModal from "../../ui/MeasuresModal";
import { CloseIcon } from "../../assets/svg/CloseIcon";
import { MEASURES_URL, MEASURES_UPDATE_URL, SCORECARD_KPI_TARGET, KPI_TARGET } from "../../api/urls";
import { putEndpoint } from "../../api/api";
import { get } from "../../utils/get";
import { toast } from "react-toastify";
import useAuth from "../../hooks/useAuth";
import FilesViewer from "../../components/FileViewer/FilesViewer";

interface Measures {
  id: string;
  name: string;
  kpi_id: string;
  scorecard_type: string;
  status: string;
  owner_name: string;
  actual_value: number;
  target: number;
  comments: string;
  evidence: string;
  filepath: string;
  evidence_type: string;
  scorecard_kpi_id: string;
  allocation_period: Number;
}

const Approvals = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMeasure, setSelectedMeasure] = useState<Measures | null>(null);
  const [measures, setMeasures] = useState<Measures[]>([]);
  const [filteredMeasures, setFilteredMeasures] = useState<Measures[]>([]);
  const [staffName, setStaffName] = useState("");
  const [scorecardType, setScorecardType] = useState("");
  const [loading, setLoading] = useState(false);
  const [isFiltersOpen, setFiltersOpen] = useState(true);
  const [modalAnimation, setModalAnimation] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const { user } = useAuth().authState;
  const userId = get(user, "id");
  const accessToken = localStorage.getItem("access_token");

  const [measureResponse, putMeasures] = putEndpoint();

  const maxLength = 50;

  useEffect(() => {
    fetchMeasures();
  }, []);

  useEffect(() => {
    filterMeasures();
  }, [staffName, scorecardType, measures]);

  useEffect(() => {
    setCurrentPage(1);
  }, [staffName, scorecardType]);

  const fetchMeasures = async () => {
    setLoading(true);
    console.log("user ID is :", userId);
    try {
      const response = await Axios.get(`${MEASURES_URL}?user_id=${userId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (response.status === 200) {
        setMeasures(response.data);
        setFilteredMeasures(response.data);
      }
    } catch (error) {
      console.error("Error fetching measures:", error);
      setMeasures([]);
    } finally {
      setLoading(false);
    }
  };

  const filterMeasures = () => {
    let filtered = measures;

    if (staffName) {
      filtered = filtered.filter((measure) =>
        measure.owner_name.toLowerCase().includes(staffName.toLowerCase())
      );
    }

    if (scorecardType) {
      filtered = filtered.filter((measure) => measure.scorecard_type === scorecardType);
    }

    setFilteredMeasures(filtered);
  };

  const handleOpenModal = (measure: Measures) => {
    setSelectedMeasure(measure);
    setModalAnimation("fadeIn"); // Apply fade-in animation
    setIsModalOpen(true);
  };
  
  const handleCloseModal = () => {
    setModalAnimation("fadeOut");
    setIsFullscreen(false); // Reset fullscreen state
    setTimeout(() => {
      setIsModalOpen(false);
      setSelectedMeasure(null);
    }, 500);
  };

  const handleFullscreenToggle = (fullscreen: boolean) => {
    setIsFullscreen(fullscreen);
  };

  useEffect(() => {
    if (measureResponse.complete) {
      console.log("PUT request completed:", measureResponse);
    }
  }, [measureResponse]);

  const handleApprove = async (values: { comments: string }) => {
    const valuesToUpdate = {
      actual_value: selectedMeasure?.actual_value,
    };
  
    try {
      // Update measure status
      await Axios.post(
        MEASURES_UPDATE_URL,
        {
          status: "approved",
          id: selectedMeasure?.id,
          comments: values.comments,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
  
      // Update KPI target
      const kpiResponse = await Axios.put(
        `${SCORECARD_KPI_TARGET}/${selectedMeasure?.scorecard_kpi_id}`,
        valuesToUpdate,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
  
      if (kpiResponse.status === 200) {
        toast.success("Measure approved");
        handleCloseModal();
        fetchMeasures();
      } else {
        toast.error("Error updating KPI actual value");
        throw new Error("PUT request failed");
      }
    } catch (error) {
      console.error("Error approving measure:", error);
      toast.error("Error approving measure");
    }
  };

  const handleReject = async (values: { comments: string }) => {
    try {
      await Axios.post(
        MEASURES_UPDATE_URL,
        {
          status: "rejected",
          id: selectedMeasure?.id,
          comments: values.comments,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      toast.success("Measure rejected");
      handleCloseModal();
      fetchMeasures();
    } catch (error) {
      console.error("Error rejecting measure:", error);
      toast.error("Error rejecting measure");
    }
  };

  const renderFileViewer = (filepath: string | undefined, isFullscreen: boolean) => {
    return <FilesViewer filepath={filepath} isFullscreen={isFullscreen} />;
  };  

  const truncateURL = (url: string, maxLength: number) => {
    if (url.length <= maxLength) return url;
    return `${url.substring(0, maxLength)}...`;
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredMeasures.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredMeasures.length / itemsPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  return (
    <div>
      <GlobalContainer>
        <HeaderContainer>
          <TitleText>Measures for Approval</TitleText>
        </HeaderContainer>
        <FiltersContainer isOpen={isFiltersOpen}>
          <FilterInput
            type="text"
            placeholder="Filter by staff name"
            value={staffName}
            onChange={(e) => setStaffName(e.target.value)}
          />
          <FilterSelect
            value={scorecardType}
            onChange={(e) => setScorecardType(e.target.value)}
          >
            <option value="" disabled>
              Filter by Scorecard Type
            </option>
            <option value="Regular">Regular</option>
            <option value="Probation">Probation</option>
            <option value="PIP">PIP</option>
          </FilterSelect>
          <ClearFiltersButton
            text="Clear Filters"
            onClick={() => {
              setStaffName("");
              setScorecardType("");
            }}
          />
        </FiltersContainer>

        {loading ? (
          <LoadingSpinner>Loading measures...</LoadingSpinner>
        ) : (
          <>
            <MeasureList>
              <MeasureHeader>
                <MeasureColumn>Name</MeasureColumn>
                <MeasureColumn>Status</MeasureColumn>
                <MeasureColumn>Scorecard Type</MeasureColumn>
                <MeasureColumn>Staff Name</MeasureColumn>
                <MeasureColumn>Actions</MeasureColumn>
              </MeasureHeader>
              {currentItems.map((measure) => (
                <MeasureItem key={measure.id}>
                  <MeasureColumn>{measure.name}</MeasureColumn>
                  <MeasureColumn>{measure.status}</MeasureColumn>
                  <MeasureColumn>{measure.scorecard_type}</MeasureColumn>
                  <MeasureColumn>{measure.owner_name}</MeasureColumn>
                  <MeasureColumn>
                    <Button text="View" onClick={() => handleOpenModal(measure)} />
                  </MeasureColumn>
                </MeasureItem>
              ))}
            </MeasureList>
            <PaginationContainer>
              <PageButton
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </PageButton>
              {[...Array(totalPages)].map((_, index) => (
                <PageButton
                  key={index + 1}
                  onClick={() => paginate(index + 1)}
                  isActive={currentPage === index + 1}
                >
                  {index + 1}
                </PageButton>
              ))}
              <PageButton
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </PageButton>
              <PageInfo>
                Page {currentPage} of {totalPages}
              </PageInfo>
            </PaginationContainer>
          </>
        )}
      </GlobalContainer>

      {isModalOpen && selectedMeasure && (
        <MeasuresModal
          title="Update Measure"
          closeModal={handleCloseModal}
          onFullscreenToggle={handleFullscreenToggle}
        >
          <Formik
            initialValues={{ comments: "" }}
            onSubmit={(values) => {
              //handleApprove(values);
            }}
          >
            {({ values, handleChange, errors }) => (
              <Form>
                <FieldSet>
                  <Label>Name</Label>
                  <StyledInputArea name="name" value={selectedMeasure.name} disabled />
                </FieldSet>
                <FieldSet>
                  <Label>Staff Name</Label>
                  <StyledInputArea name="owner_name" value={selectedMeasure.owner_name} disabled />
                </FieldSet>
                <FieldSet>
                  <Label>KPI Target</Label>
                  <StyledInputArea name="target" value={selectedMeasure.target} disabled />
                </FieldSet>
                <FieldSet>
                  <Label>Actual Value</Label>
                  <StyledInputArea name="actual_value" value={selectedMeasure.actual_value} disabled />
                </FieldSet>
                <FieldSet>
                  <Label>Allocation Period</Label>
                  <StyledInputArea name="period" value={selectedMeasure.allocation_period.toString()} disabled />
                </FieldSet>
                {selectedMeasure.evidence_type === "comment" ? (
                  <>
                    <FieldSet>
                      <Label>Evidence Comments</Label>
                      <StyledTextArea name="evidence" value={selectedMeasure.evidence} disabled />
                    </FieldSet>
                    <FieldSet>
                      <Label>Evidence Files</Label>
                      <FileViewer>
                      {renderFileViewer(selectedMeasure.filepath, isFullscreen)}
                      </FileViewer>
                    </FieldSet>
                  </>
                ) : selectedMeasure.evidence_type === "ada" ? (
                  <>
                    <FieldSet>
                      <Label>Evidence Comments</Label>
                      <StyledTextArea name="evidence" value={selectedMeasure.evidence} disabled />
                    </FieldSet>
                    <FieldSet>
                      <Label>ADA Ref Links</Label>
                      <div className="space-y-2">
                        {selectedMeasure.filepath.split(";").map((link, index) => (
                          <ADARefLink
                            key={index}
                            href={link.trim()}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {truncateURL(link.trim(), maxLength)}
                          </ADARefLink>
                        ))}
                      </div>
                    </FieldSet>
                  </>
                ) : (
                  <FieldSet>
                    <Label>No evidence available</Label>
                  </FieldSet>
                )}
                <CommentsBox>
                  <InputSection>
                    <StyledCommentsTextArea
                      name="comments"
                      placeholder="Add comments here..."
                      value={values.comments}
                      onChange={handleChange}
                      isInvalid={Boolean(errors.comments)}
                    />
                    {errors.comments && <ErrorText>{errors.comments}</ErrorText>}
                  </InputSection>
                  <ModalFooter>
                    <ApproveButton text="Approve" onClick={() => handleApprove(values)} />
                    <RejectButton text="Reject" onClick={() => handleReject(values)} />
                  </ModalFooter>
                </CommentsBox>
              </Form>
            )}
          </Formik>
        </MeasuresModal>
      )}
    </div>
  );
};

export default Approvals;

// Styled Components

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

const fadeOut = keyframes`
  from { opacity: 1; transform: scale(1); }
  to { opacity: 0; transform: scale(0.95); }
`;

const GlobalContainer = styled.div`
  padding: 20px;
  animation: ${fadeIn} 0.5s ease-out;
`;

const HeaderContainer = styled.div`
  margin-bottom: 1px;
`;

const ClearIcon = styled(CloseIcon)`
  cursor: pointer;
`;

const MeasureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MeasureHeader = styled.div`
  display: flex;
  background: #f4f4f4;
  padding: 10px;
  font-weight: bold;
`;

const MeasureItem = styled.div`
  display: flex;
  padding: 10px;
  border-bottom: 1px solid #ddd;
`;

const MeasureColumn = styled.div`
  flex: 1;
`;

const ColumnText = styled.p`
  margin: 0;
`;

const FieldSet = styled.fieldset`
  border: none;
  margin-bottom: 10px;
`;

const Label = styled.label`
  color: ${(props) => props.color || "#214160"};
  display: block;
  margin-bottom: 5px;
`;

const StyledInputArea = styled.input`
  width: 100%;
  padding: 0.5em;
  border: 1px solid #ddd;
  border-radius: 5px;
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  padding: 0.5em;
  border: 1px solid #ddd;
  border-radius: 5px;
`;

const FileViewer = styled.div`
  margin: 10px 0;
`;

const CommentsBox = styled.div`
  display: flex;
  flex-direction: column;
`;

const InputSection = styled.div`
  margin-bottom: 20px;
`;

const StyledCommentsTextArea = styled.textarea<{ isInvalid: boolean }>`
  width: 100%;
  padding: 0.5em;
  border: 1px solid ${(props) => (props.isInvalid ? "red" : "#ddd")};
  border-radius: 5px;
  animation: ${(props) => (props.isInvalid ? "shake 0.5s" : "none")};

  @keyframes shake {
    0% {
      transform: translateX(0);
    }
    25% {
      transform: translateX(-10px);
    }
    50% {
      transform: translateX(10px);
    }
    75% {
      transform: translateX(-10px);
    }
    100% {
      transform: translateX(0);
    }
  }
`;

const ErrorText = styled.div`
  color: red;
  margin-top: 5px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
`;

const ApproveButton = styled(Button)`
  background-color: green;
  color: white;
  border: none;
  padding: 0.75em 1.5em;
  border-radius: 0.75em;
  cursor: pointer;
  &:hover {
    background-color: darkgreen;
  }
`;

const RejectButton = styled(Button)`
  background-color: red;
  color: white;
  border: none;
  padding: 0.75em 1.5em;
  border-radius: 0.75em;
  cursor: pointer;
  &:hover {
    background-color: darkred;
  }
`;

const ADARefLink = styled.a`
  display: block;
  color: blue;
  text-decoration: underline;
  cursor: pointer;
  &:hover {
    text-decoration: none;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0;
  gap: 10px;
`;

const PageButton = styled.button<{ isActive?: boolean }>`
  padding: 8px 12px;
  margin: 0 3px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  background-color: ${({ isActive }) => (isActive ? "#01A8CE" : "#f1f1f1")};
  color: ${({ isActive }) => (isActive ? "#fff" : "#333")};
  font-weight: ${({ isActive }) => (isActive ? "bold" : "normal")};
  transition: background-color 0.2s;
  &:hover {
    background-color: ${({ isActive }) => (isActive ? "#01A8CE" : "#ddd")};
  }
  &:disabled {
    cursor: not-allowed;
    background-color: #e0e0e0;
    color: #888;
  }
`;

const PageInfo = styled.span`
  font-size: 14px;
  color: #666;
`;

const FiltersContainer = styled.div<{ isOpen: boolean }>`
  display: ${({ isOpen }) => (isOpen ? "flex" : "none")};
  flex-wrap: wrap;
  gap: 15px;
  align-items: center;
  padding: 10px;
  background: #f9f9f9;
  border-radius: 8px;
  margin-bottom: 15px;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 5px;
  outline: none;
  width: 200px;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 5px;
  background-color: #fff;
  outline: none;
  cursor: pointer;
`;

const ClearFiltersButton = styled(Button)`
  background: #dc3545;
  color: white;
  padding: 8px 12px;
  border-radius: 5px;
  font-size: 14px;
  &:hover {
    background: #c82333;
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 50px 0;
  font-size: 18px;
  color: #555;
`;

// Enhance the modal overlay to include a fade transition
const ModalOverlay = styled.div<{ animation: string }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${(props) => (props.animation === "fadeOut" ? fadeOut : fadeIn)} 0.5s ease-out;
  transition: opacity 0.5s ease-out;
`;

const MeasuresModalWrapper = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  width: 500px;
  max-width: 90%;
  animation: ${fadeIn} 0.5s ease-out;
  transition: transform 0.5s ease-out;
`;
